import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Swal from "sweetalert2";

import PaymentService from "services/payment.service";
import { trackThenNavigate } from "utils/track";
import { getApiErrorMessage } from "utils/api-errors";

// Evento usado por código fora do React (interceptor do Axios) para abrir o modal.
export const UPGRADE_EVENT = "dj:upgrade-required";

export const requestUpgrade = () => window.dispatchEvent(new CustomEvent(UPGRADE_EVENT));

export const PLANS = {
  monthly: { label: "Mensal", price: "R$ 39,90", period: "/mês" },
  yearly: { label: "Anual", price: "20% off", period: "no plano anual" },
};

export const PREMIUM_BENEFITS = [
  "Agenda, Eventos e Clientes",
  "Contratos com assinatura digital",
  "10 liberações de pedido por mês",
  "Seus números no painel: visualizações e conversão",
];

const UpgradeContext = createContext({
  open: false,
  openUpgrade: () => {},
  closeUpgrade: () => {},
  startCheckout: async () => {},
  openPortal: async () => {},
  pendingAction: null,
});

export function UpgradeProvider({ children }) {
  const [open, setOpen] = useState(false);
  // "monthly" | "yearly" | "portal" enquanto aguarda a API
  const [pendingAction, setPendingAction] = useState(null);

  const openUpgrade = useCallback(() => setOpen(true), []);
  const closeUpgrade = useCallback(() => setOpen(false), []);

  useEffect(() => {
    window.addEventListener(UPGRADE_EVENT, openUpgrade);
    return () => window.removeEventListener(UPGRADE_EVENT, openUpgrade);
  }, [openUpgrade]);

  const openPortal = useCallback(async () => {
    setPendingAction("portal");
    try {
      const res = await PaymentService.getPortalUrl();
      if (!res?.url) throw new Error("URL não retornada");
      window.location.href = res.url;
    } catch (error) {
      setPendingAction(null);
      if (error?.code === "no_billing_account") {
        setOpen(false);
        const result = await Swal.fire({
          icon: "info",
          title: "Assinatura",
          text: error.message || getApiErrorMessage(error),
          showCancelButton: true,
          confirmButtonText: "Ver planos",
          cancelButtonText: "Fechar",
        });
        if (result.isConfirmed) setOpen(true);
        return;
      }
      Swal.fire("Erro", getApiErrorMessage(error, "Não foi possível abrir o portal de pagamento."), "error");
    }
  }, []);

  const startCheckout = useCallback(
    async (plan) => {
      setPendingAction(plan);
      try {
        const res = await PaymentService.createSubscription(plan);
        if (!res?.url) throw new Error("URL não retornada");
        // Mantém pendingAction até sair da página
        trackThenNavigate("begin_checkout", {}, res.url);
      } catch (error) {
        setPendingAction(null);
        if (error?.code === "already_premium") {
          setOpen(false);
          const result = await Swal.fire({
            icon: "info",
            title: "Você já é Premium",
            text: error.message || "Sua assinatura já está ativa.",
            showCancelButton: true,
            confirmButtonText: "Gerenciar assinatura",
            cancelButtonText: "Fechar",
          });
          if (result.isConfirmed) openPortal();
          return;
        }
        const fallback =
          error?.httpStatus === 422 ? "Plano inválido." : "Não foi possível gerar o link de pagamento.";
        Swal.fire("Erro", getApiErrorMessage(error, fallback), "error");
      }
    },
    [openPortal]
  );

  const value = useMemo(
    () => ({ open, openUpgrade, closeUpgrade, startCheckout, openPortal, pendingAction }),
    [open, openUpgrade, closeUpgrade, startCheckout, openPortal, pendingAction]
  );

  return <UpgradeContext.Provider value={value}>{children}</UpgradeContext.Provider>;
}

UpgradeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useUpgrade = () => useContext(UpgradeContext);
